import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  api,
  JourneyBuyCompareSide,
  JourneyConversionCell,
  JourneyCohortProfile,
  JourneyCtaReach,
  JourneyFunnelStep,
  JourneyImpulseCard,
  JourneyAcquisitionChannels,
  JourneyAcquisitionEntryGroup,
  JourneyPageVersions,
  JourneyVersionRow,
  SiteAnalyticsJourney,
  SiteAnalyticsSummary,
} from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/ui/tooltip";
import DailyTrendChart from "@/components/DailyTrendChart";
import StatsExplainer from "@/components/StatsExplainer";
import JourneyEntryGroupsSplit from "@/components/JourneyEntryGroupsSplit";
import EntryGroupsSplit, {
  EntryGroupChannelList,
  type EntryGroupsSplitMode,
} from "@/components/EntryGroupsSplit";
import { cn, formatPLN } from "@/lib/utils";
import { PROFILE_LABELS_PL } from "@/lib/metricsGlossary";

const VISITOR_FALLBACK = ["new", "returning"];
const TRAFFIC_FALLBACK = [
  "organic_social",
  "paid_social",
  "direct",
  "organic_search",
  "referral",
  "cross_network",
  "other",
];
const VISITOR_LABELS: Record<string, string> = {
  new: "Nowi",
  returning: "Powracający",
};

type SortKey = "sessions" | "checkout_rate" | "purchase_rate" | "revenue";

export default function SiteFunnel() {
  const [period, setPeriod] = useState<JourneyWindowId>("7");
  const [entry, setEntry] = useState("");
  const [visitor, setVisitor] = useState("");
  const [traffic, setTraffic] = useState("");
  const [version, setVersion] = useState("");
  const [pageId, setPageId] = useState("");
  const [via, setVia] = useState("");
  const [versionMode, setVersionMode] = useState<"all" | "compare">("all");
  const [legacyOpen, setLegacyOpen] = useState(false);

  const journeyQuery = useQuery({
    queryKey: ["site-analytics-journey", period, entry, visitor, traffic, version, pageId, via],
    queryFn: () =>
      api.siteAnalyticsJourney({
        ...journeyWindowParams(period),
        entry: entry || undefined,
        visitor: visitor || undefined,
        traffic: traffic || undefined,
        version: version || undefined,
        page_id: pageId || undefined,
        via: via || undefined,
      }),
    retry: false,
  });

  const legacyDays =
    period === "today" || period === "yesterday" ? 1 : Number(period) || 7;

  const legacyQuery = useQuery({
    queryKey: ["site-analytics-summary", legacyDays],
    queryFn: () => api.siteAnalyticsSummary(legacyDays),
    enabled: legacyOpen,
  });

  const journey = journeyQuery.data;
  const filterOpts = journey?.available_filters;
  const pageOptions = useMemo(() => {
    const fromApi = filterOpts?.pages ?? [];
    const fromPv = (journey?.page_versions?.pages ?? []).map((p) => p.page_id);
    return Array.from(new Set([...fromApi, ...fromPv])).filter(Boolean);
  }, [filterOpts?.pages, journey?.page_versions?.pages]);
  const pageLabel = (pid: string) =>
    filterOpts?.page_labels?.[pid] ??
    journey?.page_versions?.pages?.find((p) => p.page_id === pid)?.label ??
    pid;
  const entryLabel = (k: string) => filterOpts?.entry_labels?.[k] ?? k;
  const trafficLabel = (k: string) => filterOpts?.traffic_labels?.[k] ?? k;

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="strona" />

      <div className="flex flex-wrap items-center gap-2">
        {JOURNEY_WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === period ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {journey?.period?.label_pl
            ? journey.period.label_pl
            : "Okno czasu: Europe/Warsaw"}
          {journey?.total_sessions != null ? ` · ${journey.total_sessions} wizyt` : ""}
        </span>
      </div>

      {journey?.ok && (
        <JourneyEntryGroupsSplit channels={journey.acquisition_channels} />
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <FilterSelect
          label="Podstrona"
          value={pageId}
          onChange={(v) => {
            setPageId(v);
            if (!v) setVersionMode("all");
          }}
          options={pageOptions}
          allLabel="Wszystkie podstrony"
          optionLabel={pageLabel}
        />
        <FilterSelect
          label="Strona wejścia"
          value={entry}
          onChange={setEntry}
          options={filterOpts?.entries ?? []}
          allLabel="Wszystkie wejścia"
          optionLabel={entryLabel}
        />
        <FilterSelect
          label="Nowi vs powracający"
          tipId="visitor_filter"
          value={visitor}
          onChange={setVisitor}
          options={filterOpts?.visitors?.length ? filterOpts.visitors : VISITOR_FALLBACK}
          allLabel="Wszyscy"
          optionLabel={(k) => VISITOR_LABELS[k] ?? k}
        />
        <FilterSelect
          label="Kanał (jak w GA4)"
          tipId="traffic_filter"
          value={traffic}
          onChange={setTraffic}
          options={filterOpts?.traffics?.length ? filterOpts.traffics : TRAFFIC_FALLBACK}
          allLabel="Wszystkie źródła"
          optionLabel={trafficLabel}
        />
        <FilterSelect
          label="Z którego linka"
          tipId="via_filter"
          value={via}
          onChange={setVia}
          options={filterOpts?.vias ?? []}
          allLabel="Wszystkie linki"
        />
        <FilterSelect
          label="Wersja strony (test)"
          tipId="version_filter"
          value={version}
          onChange={setVersion}
          options={filterOpts?.versions ?? []}
          allLabel="Wszystkie wersje"
        />
      </div>

      {journey?.ok && (
        <DailyTrendChart
          title="Wizyty dzień po dniu"
          subtitle="Nasz licznik na stronie. Reaguje na filtry i wybrane okno."
          data={(journey.daily?.days ?? []).map((d) => ({
            date: d.date,
            sessions: d.sessions ?? 0,
            cart: d.cart_sessions ?? 0,
          }))}
          series={[
            { key: "sessions", label: "Wizyty", color: "hsl(var(--brand))" },
            { key: "cart", label: "Wejścia do koszyka", color: "hsl(var(--primary))" },
          ]}
          mode="line"
        />
      )}

      {pageId && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">Tryb wersji podstrony:</span>
          <Button
            size="sm"
            variant={versionMode === "all" ? "default" : "outline"}
            onClick={() => setVersionMode("all")}
          >
            Lacznie
          </Button>
          <Button
            size="sm"
            variant={versionMode === "compare" ? "default" : "outline"}
            onClick={() => setVersionMode("compare")}
          >
            Porownaj wersje
          </Button>
          <span className="text-xs text-muted-foreground">
            Zakup SSOT EasyCart jest w Koszykach; tu CVR journey per wersja strony.
          </span>
        </div>
      )}

      {journeyQuery.isLoading && <EmptyCard text="Wczytywanie sciezek klienta..." />}
      {journeyQuery.isError && (
        <EmptyCard
          text={
            "Brak danych first-party journey (endpoint /api/site-analytics/journey). " +
            "Gdy store i API beda gotowe, ten ekran wypelni sie agregatami."
          }
        />
      )}
      {journey && !journey.ok && (
        <EmptyCard text={journey.error || "Journey analytics niedostepne w tym oknie."} />
      )}
      {journey?.ok && (
        <JourneyBody data={journey} pageId={pageId} versionMode={versionMode} />
      )}

      <LegacyGa4Block
        open={legacyOpen}
        onToggle={() => setLegacyOpen((v) => !v)}
        data={legacyQuery.data}
        isLoading={legacyQuery.isLoading}
        isError={legacyQuery.isError}
      />
    </div>
  );
}

function JourneyBody({
  data,
  pageId,
  versionMode,
}: {
  data: SiteAnalyticsJourney;
  pageId: string;
  versionMode: "all" | "compare";
}) {
  const pv = data.page_versions;
  const pageScoped = Boolean(pageId && pv?.page_id);
  const compareRows =
    pageScoped && versionMode === "compare"
      ? (pv?.by_version ?? [])
      : pageScoped && versionMode === "all"
        ? []
        : (data.version_compare?.rows ?? []);

  return (
    <div className="grid gap-5">
      <AcquisitionChannelsCard channels={data.acquisition_channels} />

      {pageScoped && pv?.all_versions && versionMode === "all" && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <PageAllVersionsCard
            label={pv.label || pageId}
            metrics={pv.all_versions}
            registryCount={pv.registry?.length ?? 0}
          />
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <ConversionMatrixBlock cells={data.conversion_matrix?.cells ?? []} />
      </motion.div>

      {(versionMode === "compare" || !pageScoped) && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 }}>
          <VersionCompareBlock
            rows={compareRows}
            title={
              pageScoped
                ? `Porownanie wersji: ${pv?.label || pageId}`
                : "Porownanie wersji tresci (globalnie)"
            }
            subtitle={
              pageScoped
                ? "Wersja z pierwszego eventu na tej podstronie (experiment_version / content_sig)."
                : "experiment_version / content_sig w czasie (nie klasyczny split A/B). Wybierz podstrone, aby porownac lokalnie."
            }
          />
        </motion.div>
      )}

      {pageScoped && (pv?.registry?.length ?? 0) > 0 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <PageRegistryBlock rows={pv?.registry ?? []} />
        </motion.div>
      )}

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
        <CohortsBlock
          profiles={data.cohorts?.profiles ?? []}
          buyVsNoBuy={data.cohorts?.buy_vs_no_buy}
          impulse={data.cohorts?.impulse_card}
        />
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
        <CtaReachBlock cta={data.content_reach?.cta ?? []} />
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}>
        <SessionFunnelBlock steps={data.session_funnel?.steps ?? []} />
      </motion.div>
    </div>
  );
}

function ConversionMatrixBlock({ cells }: { cells: JourneyConversionCell[] }) {
  const [minSessions, setMinSessions] = useState(5);
  const [sortKey, setSortKey] = useState<SortKey>("purchase_rate");
  const [sortAsc, setSortAsc] = useState(false);

  const filtered = useMemo(() => {
    const list = cells.filter((c) => (c.sessions ?? 0) >= minSessions);
    list.sort((a, b) => {
      const av = Number(a[sortKey] ?? 0);
      const bv = Number(b[sortKey] ?? 0);
      return sortAsc ? av - bv : bv - av;
    });
    return list;
  }, [cells, minSessions, sortKey, sortAsc]);

  const bestPurchaseByEntry = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of filtered) {
      const prev = map.get(c.entry_page) ?? -1;
      if ((c.purchase_rate ?? 0) > prev) map.set(c.entry_page, c.purchase_rate ?? 0);
    }
    return map;
  }, [filtered]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <div>
          <CardTitle>Macierz konwersji</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Które połączenie strona + źródło ruchu najlepiej sprzedaje
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Wej?cie x visitor x klasa ruchu. Tylko agregaty.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Min. sesji
          <input
            type="number"
            min={1}
            className="h-8 w-16 rounded-md border border-input bg-background px-2 font-mono"
            value={minSessions}
            onChange={(e) => setMinSessions(Math.max(1, Number(e.target.value) || 1))}
          />
        </label>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak komórek przy tym progu sesji.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Wej?cie</th>
                  <th className="py-2 pr-3 font-medium">Visitor</th>
                  <th className="py-2 pr-3 font-medium">Ruch</th>
                  <SortTh active={sortKey === "sessions"} asc={sortAsc} onClick={() => toggleSort("sessions")}>
                    Sesje
                  </SortTh>
                  <SortTh
                    active={sortKey === "checkout_rate"}
                    asc={sortAsc}
                    onClick={() => toggleSort("checkout_rate")}
                  >
                    CVR checkout
                  </SortTh>
                  <SortTh
                    active={sortKey === "purchase_rate"}
                    asc={sortAsc}
                    onClick={() => toggleSort("purchase_rate")}
                  >
                    CVR purchase
                  </SortTh>
                  <SortTh active={sortKey === "revenue"} asc={sortAsc} onClick={() => toggleSort("revenue")}>
                    Przychód
                  </SortTh>
                  <th className="py-2 font-medium">Impulse %</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => {
                  const isBest =
                    (c.purchase_rate ?? 0) > 0 &&
                    bestPurchaseByEntry.get(c.entry_page) === (c.purchase_rate ?? 0);
                  return (
                    <tr
                      key={`${c.entry_page}-${c.visitor_type}-${c.traffic_label || c.traffic_class}-${i}`}
                      className={cn("border-b border-border/60", isBest && "bg-brand/5")}
                    >
                      <td className="py-2 pr-3 font-medium">{c.entry_label || c.entry_page}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{c.visitor_type}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{c.traffic_label || c.traffic_class}</td>
                      <td className="py-2 pr-3 font-mono tabular-nums">{c.sessions}</td>
                      <td className="py-2 pr-3 font-mono tabular-nums">{pct(c.checkout_rate)}</td>
                      <td
                        className={cn(
                          "py-2 pr-3 font-mono tabular-nums",
                          isBest && "font-semibold text-brand",
                        )}
                      >
                        {pct(c.purchase_rate)}
                        {isBest && (
                          <Badge variant="secondary" className="ml-2 align-middle text-[10px]">
                            best
                          </Badge>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-mono tabular-nums">
                        {c.revenue != null ? formatPLN(c.revenue) : "-"}
                      </td>
                      <td className="py-2 font-mono tabular-nums">
                        {c.impulse_buy_share != null ? pct(c.impulse_buy_share) : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function VersionCompareBlock({
  rows,
  title = "Porownanie wersji tresci",
  subtitle = "experiment_version / content_sig w czasie (nie klasyczny split A/B).",
}: {
  rows: JourneyVersionRow[];
  title?: string;
  subtitle?: string;
}) {
  const bestIdx = useMemo(() => {
    let best = -1;
    let bestRate = -1;
    rows.forEach((r, i) => {
      if ((r.sessions ?? 0) >= 10 && (r.purchase_rate ?? 0) > bestRate) {
        bestRate = r.purchase_rate ?? 0;
        best = i;
      }
    });
    return best;
  }, [rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak wersji w wybranym oknie.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Wersja</th>
                  <th className="py-2 pr-3 font-medium">content_sig</th>
                  <th className="py-2 pr-3 font-medium">Sesje</th>
                  <th className="py-2 pr-3 font-medium">CVR checkout</th>
                  <th className="py-2 pr-3 font-medium">CVR purchase</th>
                  <th className="py-2 pr-3 font-medium">Impulse %</th>
                  <th className="py-2 pr-3 font-medium">Accidental %</th>
                  <th className="py-2 pr-3 font-medium">Med. scroll</th>
                  <th className="py-2 font-medium">Med. do CTA</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${r.experiment_version}-${r.content_sig ?? ""}-${i}`}
                    className={cn("border-b border-border/60", i === bestIdx && "bg-brand/5")}
                  >
                    <td className="py-2 pr-3 font-medium">
                      {r.experiment_version || "(brak)"}
                      {i === bestIdx && (
                        <Badge variant="secondary" className="ml-2 text-[10px]">
                          lepsza
                        </Badge>
                      )}
                    </td>
                    <td className="max-w-[120px] truncate py-2 pr-3 font-mono text-xs text-muted-foreground">
                      {r.content_sig || "-"}
                    </td>
                    <td className="py-2 pr-3 font-mono tabular-nums">{r.sessions}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums">{pct(r.checkout_rate)}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums">{pct(r.purchase_rate)}</td>
                    <td className="py-2 pr-3 font-mono tabular-nums">
                      {r.impulse_buy_rate != null ? pct(r.impulse_buy_rate) : "-"}
                    </td>
                    <td className="py-2 pr-3 font-mono tabular-nums">
                      {r.accidental_exit_rate != null ? pct(r.accidental_exit_rate) : "-"}
                    </td>
                    <td className="py-2 pr-3 font-mono tabular-nums">
                      {r.median_scroll != null ? `${Math.round(r.median_scroll)}%` : "-"}
                    </td>
                    <td className="py-2 font-mono tabular-nums">{fmtMs(r.median_ms_to_cta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PageAllVersionsCard({
  label,
  metrics,
  registryCount,
}: {
  label: string;
  metrics: JourneyVersionRow;
  registryCount: number;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lacznie: {label}</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Wszystkie wersje tej podstrony w oknie. Rejestr: {registryCount} wpisow.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Wizyty" value={String(metrics.sessions)} />
          <Stat label="Do koszyka (%)" value={pct(metrics.checkout_rate)} />
          <Stat label="Do zakupu (%)" value={pct(metrics.purchase_rate)} />
          <Stat
            label="Ile przewijają (mediana)"
            value={metrics.median_scroll != null ? `${Math.round(metrics.median_scroll)}%` : "-"}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function PageRegistryBlock({ rows }: { rows: NonNullable<JourneyPageVersions["registry"]> }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Rejestr wersji podstrony</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Auto-wpisy z beaconow (page_id + experiment_version + content_sig).
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Wersja</th>
                <th className="py-2 pr-3 font-medium">content_sig</th>
                <th className="py-2 pr-3 font-medium">Eventy</th>
                <th className="py-2 pr-3 font-medium">Pierwszy</th>
                <th className="py-2 pr-3 font-medium">Ostatni</th>
                <th className="py-2 font-medium">Notatka</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.experiment_version}-${r.content_sig ?? ""}-${i}`} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-medium">{r.experiment_version}</td>
                  <td className="max-w-[120px] truncate py-2 pr-3 font-mono text-xs text-muted-foreground">
                    {r.content_sig || "-"}
                  </td>
                  <td className="py-2 pr-3 font-mono tabular-nums">{r.event_count ?? 0}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.first_seen_at || "-"}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.last_seen_at || "-"}</td>
                  <td className="py-2 text-xs text-muted-foreground">{r.note || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function CohortsBlock({
  profiles,
  buyVsNoBuy,
  impulse,
}: {
  profiles: JourneyCohortProfile[];
  buyVsNoBuy?: {
    purchased: JourneyBuyCompareSide;
    no_purchase: JourneyBuyCompareSide;
  };
  impulse?: JourneyImpulseCard | null;
}) {
  return (
    <div className="grid gap-5">
      <Card>
        <CardHeader>
          <CardTitle>Profile zachowa?</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Jedna g?ówna kohorta na sesj? (agregat w okresie + filtrach).
          </p>
        </CardHeader>
        <CardContent>
          {profiles.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak profili.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {profiles.map((p) => (
                <div key={p.profile} className="rounded-md border border-border/70 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium">{PROFILE_LABELS_PL[p.profile] ?? p.profile}</div>
                    <Badge variant="secondary">{pct(p.share)}</Badge>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                    <span>
                      Sesje: <Mono>{p.sessions}</Mono>
                    </span>
                    <span>
                      Scroll:{" "}
                      <Mono>{p.median_scroll != null ? `${Math.round(p.median_scroll)}%` : "-"}</Mono>
                    </span>
                    <span>
                      Czas: <Mono>{fmtMs(p.median_active_ms)}</Mono>
                    </span>
                    <span>
                      Do CTA: <Mono>{fmtMs(p.median_ms_to_cta)}</Mono>
                    </span>
                    <span>
                      CVR checkout:{" "}
                      <Mono>{p.checkout_rate != null ? pct(p.checkout_rate) : "-"}</Mono>
                    </span>
                    <span>
                      CVR purchase:{" "}
                      <Mono>{p.purchase_rate != null ? pct(p.purchase_rate) : "-"}</Mono>
                    </span>
                  </div>
                  {p.top_exit_sections && p.top_exit_sections.length > 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Top exit: {p.top_exit_sections.slice(0, 3).join(", ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {buyVsNoBuy && (
        <Card>
          <CardHeader>
            <CardTitle>Kupuj?ce vs niekupuj?ce</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              <BuySide title="Z zakupem" side={buyVsNoBuy.purchased} />
              <BuySide title="Bez zakupu" side={buyVsNoBuy.no_purchase} />
            </div>
          </CardContent>
        </Card>
      )}

      {impulse && (
        <Card className="border-brand/30">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>Impuls z social / ads</CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                Udzia? impulse_buy w?ród zakupów.
              </p>
            </div>
            <div className="text-2xl font-bold tabular-nums text-brand">
              {pct(impulse.impulse_share_of_purchases)}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 text-sm sm:grid-cols-3">
              <Metric label="Ogó?em w?ród zakupów" value={pct(impulse.impulse_share_of_purchases)} />
              <Metric
                label="Z social"
                value={impulse.impulse_share_social != null ? pct(impulse.impulse_share_social) : "-"}
              />
              <Metric
                label="Z ads"
                value={impulse.impulse_share_ads != null ? pct(impulse.impulse_share_ads) : "-"}
              />
            </div>
            {impulse.note && <p className="mt-3 text-xs text-muted-foreground">{impulse.note}</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BuySide({ title, side }: { title: string; side: JourneyBuyCompareSide }) {
  return (
    <div className="rounded-md border border-border/70 p-3">
      <div className="font-medium">{title}</div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        <span>
          Sesje: <Mono>{side.sessions}</Mono>
        </span>
        <span>
          Scroll:{" "}
          <Mono>{side.median_scroll != null ? `${Math.round(side.median_scroll)}%` : "-"}</Mono>
        </span>
        <span>
          Czas: <Mono>{fmtMs(side.median_active_ms)}</Mono>
        </span>
        <span>
          Do CTA: <Mono>{fmtMs(side.median_ms_to_cta)}</Mono>
        </span>
        <span>
          Sekcje (med.):{" "}
          <Mono>{side.distinct_sections_median != null ? side.distinct_sections_median : "-"}</Mono>
        </span>
      </div>
      {side.top_sections && side.top_sections.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Top sekcje: {side.top_sections.slice(0, 4).join(", ")}
        </p>
      )}
    </div>
  );
}

function CtaReachBlock({ cta }: { cta: JourneyCtaReach[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>CTA</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Kupuję vs dowiedz się więcej (agregat). Sekcje: zakładka „Sekcje”.
        </p>
      </CardHeader>
      <CardContent>
        {!cta || cta.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak kliknięć CTA w oknie.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {cta.map((c, i) => (
              <li
                key={`${c.intent}-${c.label ?? ""}-${i}`}
                className="flex items-center justify-between gap-3"
              >
                <span className="truncate text-muted-foreground">{c.label || c.intent}</span>
                <span className="shrink-0 font-mono text-xs tabular-nums">
                  {c.clicks} · {pct(c.share)} · {fmtMs(c.median_ms_to_click)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function SessionFunnelBlock({ steps }: { steps: JourneyFunnelStep[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Lejek przebiegu sesji</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">Agregat kroków w sesji (nie lista sesji).</p>
      </CardHeader>
      <CardContent>
        {steps.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak kroków lejka.</p>
        ) : (
          <ol className="space-y-2">
            {steps.map((s, i) => (
              <li key={`${s.step}-${i}`} className="flex items-center gap-3 text-sm">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-xs">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 truncate">{s.label || s.step}</span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">
                  {s.sessions} · {pct(s.rate)}
                </span>
                <div className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-muted sm:block">
                  <div
                    className="h-full rounded-full bg-brand/70"
                    style={{ width: `${Math.min(100, Math.max(0, s.rate * 100))}%` }}
                  />
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function LegacyGa4Block({
  open,
  onToggle,
  data,
  isLoading,
  isError,
}: {
  open: boolean;
  onToggle: () => void;
  data?: SiteAnalyticsSummary;
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <Card className="border-dashed">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">Google / Clarity (stare liczniki)</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Stare liczniki Google, tylko do porównania. Prawda o sprzedaży jest w EasyCart (Koszyki /
            Sprzedaż).
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onToggle}>
          {open ? "Zwiń" : "Rozwiń"}
        </Button>
      </CardHeader>
      {open && (
        <CardContent>
          {isLoading && <p className="text-sm text-muted-foreground">Wczytywanie…</p>}
          {isError && (
            <p className="text-sm text-muted-foreground">Nie udało się wczytać Google / Clarity.</p>
          )}
          {data && <LegacyBody data={data} />}
        </CardContent>
      )}
    </Card>
  );
}

function LegacyBody({ data }: { data: SiteAnalyticsSummary }) {
  const { ga4, clarity, commerce } = data;

  return (
    <div className="grid gap-5">
      <div>
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-semibold">Prawda sprzedażowa: EasyCart vs Google</h3>
          {!commerce.ok && <Badge variant="warning">brak danych</Badge>}
        </div>
        {commerce.ok ? (
          <div className="grid gap-4 sm:grid-cols-4">
            <Metric label="Zamówienia opłacone" value={String(commerce.real_paid_orders ?? 0)} />
            <Metric label="Przychód" value={formatPLN(commerce.real_revenue ?? 0)} />
            <Metric label="Zakupy w Google" value={String(commerce.ga4_purchase_events ?? 0)} />
            <Metric
              label={
                <span className="inline-flex items-center gap-1">
                  Różnica Google vs EasyCart
                  <InfoTip tipId="ga4_vs_real" />
                </span>
              }
              value={String(commerce.ga4_vs_real_delta ?? 0)}
              tone={(commerce.ga4_vs_real_delta ?? 0) === 0 ? "neutral" : "warning"}
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{commerce.error}</p>
        )}
        {commerce.note && <p className="mt-2 text-xs text-muted-foreground">{commerce.note}</p>}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div>
          <h3 className="mb-2 text-sm font-semibold">Top źródła ruchu (Google)</h3>
          {ga4.ok && (ga4.top_traffic_sources?.length ?? 0) > 0 ? (
            <ul className="space-y-2 text-sm">
              {ga4.top_traffic_sources!.slice(0, 8).map((row, i) => (
                <li key={i} className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {String(row.sessionSource ?? "?")} / {String(row.sessionMedium ?? "?")}
                  </span>
                  <span className="font-mono tabular-nums">{String(row.sessions ?? 0)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{ga4.error || "Brak danych."}</p>
          )}
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold">Top landing pages (GA4)</h3>
          {ga4.ok && (ga4.top_landing_pages?.length ?? 0) > 0 ? (
            <ul className="space-y-2 text-sm">
              {ga4.top_landing_pages!.slice(0, 8).map((row, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <span className="truncate text-muted-foreground">
                    {String(row.landingPagePlusQueryString ?? "?")}
                  </span>
                  <span className="font-mono tabular-nums">{String(row.sessions ?? 0)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{ga4.error || "Brak danych."}</p>
          )}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <h3 className="text-sm font-semibold">Microsoft Clarity</h3>
          {!clarity.ok && <Badge variant="warning">nieskonfigurowane</Badge>}
        </div>
        {clarity.ok ? (
          <div>
            <p className="text-sm text-muted-foreground">{clarity.note}</p>
            {clarity.quota && (
              <p className="mt-2 text-xs text-muted-foreground">
                Zapytania API dzisiaj: {clarity.quota.requests_used_today}/
                {clarity.quota.self_imposed_limit}
              </p>
            )}
            <p className="mt-2 text-sm">
              Panel:{" "}
              <a
                className="underline"
                href="https://clarity.microsoft.com"
                target="_blank"
                rel="noreferrer"
              >
                clarity.microsoft.com
              </a>
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{clarity.error}</p>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/60 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-lg tabular-nums">{value}</div>
    </div>
  );
}

function FilterSelect({
  label,
  tipId,
  value,
  onChange,
  options,
  allLabel,
  optionLabel,
}: {
  label: string;
  tipId?: import("@/lib/metricsGlossary").MetricTipId;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  allLabel: string;
  optionLabel?: (v: string) => string;
}) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        {label}
        {tipId && <InfoTip tipId={tipId} />}
      </span>
      <select
        className="h-10 rounded-md border border-input bg-background px-3"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {optionLabel ? optionLabel(o) : o}
          </option>
        ))}
      </select>
    </label>
  );
}

function SortTh({
  children,
  active,
  asc,
  onClick,
}: {
  children: ReactNode;
  active: boolean;
  asc: boolean;
  onClick: () => void;
}) {
  return (
    <th className="py-2 pr-3 font-medium">
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={onClick}
      >
        {children}
        {active && <span className="text-[10px]">{asc ? "?" : "?"}</span>}
      </button>
    </th>
  );
}

function Metric({
  label,
  value,
  tone = "neutral",
}: {
  label: ReactNode;
  value: string;
  tone?: "neutral" | "warning";
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={"text-xl font-bold tabular-nums " + (tone === "warning" ? "text-warning" : "")}>
        {value}
      </div>
    </div>
  );
}

function Mono({ children }: { children: ReactNode }) {
  return <span className="font-mono tabular-nums text-foreground">{children}</span>;
}

function AcquisitionChannelsCard({ channels }: { channels?: JourneyAcquisitionChannels }) {
  const [mode, setMode] = useState<EntryGroupsSplitMode>("all");
  const paidGroup = channels?.entry_groups?.paid;
  const otherGroup = channels?.entry_groups?.other;
  const hasChannels =
    (paidGroup?.all_visits?.length ?? 0) > 0 || (otherGroup?.all_visits?.length ?? 0) > 0;
  if (!hasChannels) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            Szczegóły kanałów (GA4)
            <InfoTip tipId="acquisition_channels" />
          </CardTitle>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant={mode === "new" ? "default" : "outline"} onClick={() => setMode("new")}>
              Nowe wizyty
            </Button>
            <Button size="sm" variant={mode === "all" ? "default" : "outline"} onClick={() => setMode("all")}>
              Wszystkie wizyty
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid gap-4 lg:grid-cols-2">
          {paidGroup && (paidGroup.total_sessions ?? 0) > 0 && (
            <EntryGroupChannelList group={paidGroup} mode={mode} title="Płatne wejścia: kanały" />
          )}
          {otherGroup && (otherGroup.total_sessions ?? 0) > 0 && (
            <EntryGroupChannelList group={otherGroup} mode={mode} title="Pozostałe: kanały" />
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

function EmptyCard({ text }: { text: string }) {
  return (
    <Card>
      <CardContent className="py-8 text-center text-sm text-muted-foreground">{text}</CardContent>
    </Card>
  );
}

function pct(rate: number | null | undefined): string {
  if (rate == null || Number.isNaN(rate)) return "-";
  return `${(rate * 100).toFixed(1)}%`;
}

function fmtMs(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return "-";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  return `${(s / 60).toFixed(1)} min`;
}
